import {FC, lazy, useEffect } from "react";
import GlobalStyle from "styles/global";
import _data from "./data/data.json";
import { Helmet } from "react-helmet";
import amplitude from "amplitude-js";
import TagManager from "react-gtm-module";
import "index.css";
import find from "lodash/find";
import { _capitalizeWords } from "./utils/misc";
import drugparams from "./data/drugterms.json";
import { tagMangerId } from "./utils/environmentUrls";
import { IData, IDataMetadata, IDataHeader, IDataHero, IDataInfoIconBlock, IDataSeparation, IDataWhyZinniaBlock, IDataleadingTreatmentBlock, IDataInsuranceBlock, IDataVideoSection, IDataBadgesBlock, IDataTreatmentBlock, IDataQuotesBlock, IDataCarouselQuotes, IDataFreeSupportBlock, IDataFooter, ISource } from "types";

const Footer = lazy(() => import("components/Partials/Footer"));
const Separation = lazy(() => import("components/Partials/Separation"));
const Header = lazy(() => import("components/Header/Header"));
const Hero = lazy(() => import("components/Hero/Hero"));
const SUInfoBlock = lazy(() => import("components/SUInfoBlock"));
const InfoIconBlock = lazy(() => import("components/InfoIconBlock"));
const WhyZinniaBlock = lazy(() => import("components/WhyZinniaBlock"));
const LeadingTreatmentBlock = lazy(() => import("components/LeadingTreatmentBlock"));
const TreatmentBlock = lazy(() => import("components/TreatmentBlock"));
const BadgesBlock = lazy(() => import("components/BadgesBlock"));
const VideoSection = lazy(() => import("components/VideoSection"));
const FreeSupportBlock = lazy(() => import("components/FreeSupportBlock"));
const InsuranceBlock = lazy(() => import("components/InsuranceBlock"));
const QuotesBlock = lazy(() => import("components/QuotesBlock"));
const CarouselQuotes = lazy(() => import("components/CarouselQuotes"));

const tagManagerArgs = {
  gtmId: tagMangerId,
};

TagManager.initialize(tagManagerArgs);

const data = _data as IData[];
declare global {
  interface Window {
    intercomSettings: any;
  }
}

// Ad dynamic terms
const queryString = window.location.search;
const urlParams = new URLSearchParams(queryString);

let drugTerm;
let titleObj;
let allTerms;

let adDataWebsite = find(drugparams, function (n) {
  if (n.website === process.env.REACT_APP_WEBSITE) {
    return n;
  }
});

if (adDataWebsite) {
  allTerms = adDataWebsite.terms;
}

if (urlParams.has("adgid")) {
  titleObj = find(allTerms, (o) => {
    return o.id === urlParams.get("adgid");
  });
  if (titleObj) {
    let capWordsh1 = _capitalizeWords(titleObj.drug);
    drugTerm = capWordsh1;
  }
}

const App: FC<any> = () => {
  const website = process.env.REACT_APP_WEBSITE;
  const isIndiana = website === "Indiana"? true : false;
  const isNewport = website === "Newport" ? true : false;
  const isSerenity = website === "Serenity"? true : false;
  const isDenver = website === "Denver"? true : false;
  const websiteData = data.find(e => e.website === website).data;

  const metadataData = websiteData.find( e => e.component === "Metadata" ).information as IDataMetadata;
  const headerData = websiteData.find( e => e.component === "Header" ).information as IDataHeader;
  const heroData = websiteData.find( e => e.component === "Hero" ).information as IDataHero;
  const infoIconBlockData = websiteData.find( e => e.component === "InfoIconBlock" ).information as IDataInfoIconBlock;
  const separationData = websiteData.find( e => e.component === "Separation" ).information as IDataSeparation;
  const whyZinniaBlockData = website === "Denver" ? websiteData.find( e => e.component === "WhyZinniaBlock" ).information as IDataWhyZinniaBlock : {} as IDataWhyZinniaBlock;
  const leadingTreatmentBlockData = websiteData.find( e => e.component === "LeadingTreatmentBlock" ).information as IDataleadingTreatmentBlock;
  const insuranceBlockData = websiteData.find( e => e.component === "InsuranceBlock" ).information as IDataInsuranceBlock;
  const badgesBlockData = websiteData.find( e => e.component === "BadgesBlock" ).information as IDataBadgesBlock;
  const videoSectionData = website === "Newport" ? websiteData.find( e => e.component === "VideoSection" ).information as IDataVideoSection : {} as IDataVideoSection;
  const treatmentBlockData = websiteData.find( e => e.component === "TreatmentBlock" ).information as IDataTreatmentBlock;
  const quotesBlockData = website === "Denver" ? {} as IDataQuotesBlock : websiteData.find( e => e.component === "QuotesBlock" ).information as IDataQuotesBlock;
  const carouselQuotesData = website === "Denver" ? data.find(e => e.website === website).data.find( e => e.component === "CarouselQuotes" ).information as IDataCarouselQuotes  : {} as IDataCarouselQuotes;
  const freeSupportBlockData = websiteData.find( e => e.component === "FreeSupportBlock" ).information as IDataFreeSupportBlock;
  const footerData = websiteData.find( e => e.component === "Footer" ).information as IDataFooter;

  useEffect(() => {
    if (!window.intercomSettings) {
      window.intercomSettings = {
        app_id: "dbktifl9",
        vertical_padding: 100,
      };
    }
  }, []);

  useEffect(() => {
    const API_KEY = process.env.REACT_APP_AMPLITUDE_API_KEY;
    const PAGE = process.env.REACT_APP_WEBSITE;
    if (API_KEY && PAGE) {
      amplitude.getInstance().init(API_KEY);
      const event = "LandingPageView";
      const data = {
        page: website ? "Zinnia " + website : "Zinnia " + PAGE,
        website: window.location.pathname,
        action: "Page View",
      };
      amplitude.getInstance().logEvent(event, data);
    }
  }, [website]);

  return (
    <>
      <GlobalStyle />
      <Helmet>
        <title>{metadataData.title}</title>
        <meta content={metadataData.title} property="og:title" />
        <meta content={metadataData.title} property="twitter:title" />
        <meta name="robots" content="nofollow, noindex" />      
        <link
          rel="preconnect"
          crossOrigin="anonymous"
          href="https://fonts.googleapis.com"
        />
        <link
          href="https://fonts.googleapis.com/css2?family=Roboto+Flex:opsz,wght@8..144,300;8..144,400;8..144,500;8..144,600;8..144,700&display=swap"
          rel="stylesheet"
        />
        <link
          rel="icon"
          href="https://cdn.zinniahealth.com/wp-content/uploads/20220214183452/cropped-zinnia-favicon-1-32x32-1.png"
          sizes="32x32"
        />
        <link
          rel="icon"
          href="https://cdn.zinniahealth.com/wp-content/uploads/20220214183451/cropped-zinnia-favicon-1-192x192-1.png"
          sizes="192x192"
        />
        <link
          rel="apple-touch-icon"
          href="https://cdn.zinniahealth.com/wp-content/uploads/20220214183647/cropped-zinnia-favicon-1-180x180-1.png"
        />
      </Helmet>

      <Header
        logo={headerData.logo}
        altLogo={headerData.altLogo}
        ctaText={headerData.ctaText}
        ctaPhoneText={headerData.ctaPhoneText}
        ctaPhoneLink={headerData.ctaPhoneLink}
        ctaColor={headerData.ctaColor}
        ctaBackgroundColor={headerData.ctaBackgroundColor}
        ctaSeparatorColor={headerData.ctaSeparatorColor}
        hasLink={headerData.href}
        backgroundHeader={headerData.backgroundHeader}
        website={website}
      />

      <Hero
        heroTitle={heroData.heroTitle}
        titleColor={heroData.titleColor}
        reviewsCountMessage={heroData.reviewsCountMessage}
        subHeadingColor={heroData.subHeadingColor}
        heroTitleText={heroData.heroTitleText}
        ctaText={heroData.ctaText}
        ctaPhoneText={heroData.ctaPhoneText}
        ctaPhoneLink={heroData.ctaPhoneLink}
        ctaBackground={heroData.ctaBackground}
        ctaSeparator={heroData.ctaSeparator}
        heroBackground={heroData.heroBackground} 
        heroImage={heroData.heroImage}
        heroImageDesktop={heroData.heroImageDesktop}
        gradient={heroData.gradient}
        altImage={heroData.altImage}
        isSerenity={isSerenity}
        isNewport={isNewport}
        isIndiana={isIndiana}
        isDenver={isDenver}
        showPhone={true}
        website={website}
      />
      <SUInfoBlock
        drugTerm={drugTerm}
        color={heroData.titleColor}
        paragraphColor={heroData.titleColor}
        ctaText={"CALL TO LEARN MORE"}
        ctaPhoneText={heroData.ctaPhoneText}
        ctaPhoneLink={heroData.ctaPhoneLink}
        ctaBackground={heroData.ctaBackground}
        ctaSeparator={heroData.ctaSeparator}
        heroText={heroData.heroText}
      />
      {!isDenver ? (
        <>
        <InfoIconBlock
          mainColor={infoIconBlockData.mainColor}
          mainTitle={infoIconBlockData.mainTitle}
          iconInfo1={infoIconBlockData.iconInfo1}
          iconInfo2={infoIconBlockData.iconInfo2}
          iconInfo3={infoIconBlockData.iconInfo3}
          iconInfo4={infoIconBlockData.iconInfo4}
          iconInfo1v2={infoIconBlockData.iconInfo1v2}
          iconInfo2v2={infoIconBlockData.iconInfo2v2}
          iconInfo3v2={infoIconBlockData.iconInfo3v2}
          iconInfo4v2={infoIconBlockData.iconInfo4v2}
        />
        <Separation height={separationData.height} color={separationData.color} />
        </>
      ):(
        <WhyZinniaBlock
          color={whyZinniaBlockData.color}
          title={whyZinniaBlockData.title}
          reasons={whyZinniaBlockData.reasons}
          ctaPhoneText={whyZinniaBlockData.ctaPhoneText}
          ctaPhoneLink={whyZinniaBlockData.ctaPhoneLink}
          ctaBackground={whyZinniaBlockData.ctaBackground}
        />
      )
      }

      <LeadingTreatmentBlock
        title={leadingTreatmentBlockData.title}
        mainColor={leadingTreatmentBlockData.mainColor}
        reasons = {leadingTreatmentBlockData.reasons}
        rateComparison={leadingTreatmentBlockData.rateComparison}
        ctaText={leadingTreatmentBlockData.ctaText}
        ctaPhoneText={leadingTreatmentBlockData.ctaPhoneText}
        ctaPhoneLink={leadingTreatmentBlockData.ctaPhoneLink}
        ctaBackground={leadingTreatmentBlockData.ctaBackground}
        satisfactionBackground={leadingTreatmentBlockData.satisfactionBackground}
        satisfactionRate={leadingTreatmentBlockData.satisfactionRate}
      />

      <InsuranceBlock
        title={insuranceBlockData.title}
        content={insuranceBlockData.content}
        backgroundColor={insuranceBlockData.backgroundColor}
        insuranceBackgroundColor={insuranceBlockData.insuranceBackgroundColor}
        ctaBackground={insuranceBlockData.ctaBackground}
        ctaSeparator={insuranceBlockData.ctaSeparator}
        code={insuranceBlockData.code as ISource}
        website={website}
        isNewport={isNewport}
      />

      <BadgesBlock
        title={badgesBlockData.title}
        titleMobile={badgesBlockData.titleMobile}
        description={badgesBlockData.description}
        backgroundColor={badgesBlockData.backgroundColor}
      />
      {(isNewport) && 
        <VideoSection
          videoSrc={videoSectionData.videoSrc}
          previewSrc={videoSectionData.previewSrc}
          title={videoSectionData.title}
          content={videoSectionData.content}
          ctaText={videoSectionData.ctaText}
          ctaPhoneText={videoSectionData.ctaPhoneText}
          ctaPhoneLink={videoSectionData.ctaPhoneLink}
          ctaBackground={videoSectionData.ctaBackground}
          color={videoSectionData.color}
          ctaHasNumber={ false }
        />
      }
      <TreatmentBlock
        mainColor={treatmentBlockData.mainColor}
        subTitleColor={treatmentBlockData.subTitleColor}
        paragraphColor={treatmentBlockData.paragraphColor}
        alignment1={treatmentBlockData.alignment1}
        title1={treatmentBlockData.title1}
        content1={treatmentBlockData.content1}
        image1={treatmentBlockData.image1}
        alignment2={treatmentBlockData.alignment2}
        title2={treatmentBlockData.title2}
        content2={treatmentBlockData.content2}
        image2={treatmentBlockData.image2}
        title3={treatmentBlockData.title3}
        content3={treatmentBlockData.content3}
        image3={treatmentBlockData.image3}
        amenities={treatmentBlockData.amenities}
        ctaText={treatmentBlockData.ctaText}
        ctaPhoneText={treatmentBlockData.ctaPhoneText}
        ctaPhoneLink={treatmentBlockData.ctaPhoneLink}
        ctaBackground={treatmentBlockData.ctaBackground}
        website={website}
        isDenver={isDenver}
      />

      {!isDenver?(
        <QuotesBlock
          mainColor={quotesBlockData.mainColor}
          title={quotesBlockData.title}
          stars={quotesBlockData.stars}
          reviews={quotesBlockData.reviews}
          quote1={quotesBlockData.quote1}
          quote2={quotesBlockData.quote2}
          quote3={quotesBlockData.quote3}
          quote1by={quotesBlockData.quote1by}
          quote2by={quotesBlockData.quote2by}
          quote3by={quotesBlockData.quote3by}
        />
      ) : (
        <CarouselQuotes
          mainColor={carouselQuotesData.mainColor}
          title={carouselQuotesData.title}
          stars={carouselQuotesData.stars}
          quote1={carouselQuotesData.quote1}
          quote2={carouselQuotesData.quote2}
          quote3={carouselQuotesData.quote3}
          quote1by={carouselQuotesData.quote1by}
          quote2by={carouselQuotesData.quote2by}
          quote3by={carouselQuotesData.quote3by}
          quotePhoto1={carouselQuotesData.quotePhoto1}
          quotePhoto2={carouselQuotesData.quotePhoto2}
          quotePhoto3={carouselQuotesData.quotePhoto3}
          ctaText={carouselQuotesData.ctaText}
          ctaPhoneLink={carouselQuotesData.ctaPhoneLink}
          ctaPhoneText={carouselQuotesData.ctaPhoneText}
          ctaBackground={carouselQuotesData.ctaBackground}
        />
      )
      }
      <FreeSupportBlock
        title={freeSupportBlockData.title}
        mainColor={freeSupportBlockData.mainColor}
        ctaText={freeSupportBlockData.ctaText}
        ctaPhoneText={freeSupportBlockData.ctaPhoneText}
        ctaPhoneLink={freeSupportBlockData.ctaPhoneLink}
        ctaBackground={freeSupportBlockData.ctaBackground}
        photoGallery={freeSupportBlockData.photoGallery}
      />


      <Footer
        background={footerData.background}
        legitScriptId={footerData.legitScriptId}
        copyright={footerData.copyright}
        termsAndConditionsLink={footerData.termsAndConditionsLink}
        privacyPolicyLink={footerData.privacyPolicyLink}
        pageName={footerData.pageName}
      />
    </>
  );
};

export default App;
